import { getPaymentMethods } from 'Api/AccountApi';
import { getErrorMessageFromCode } from 'Api/EvergentApi';
import { setPaymentTypeProperty, setSubscriptionStateProperty } from 'Utils/Analytics/Amplitude';
import { getEntitlements } from 'State/User';
import { resetShippingAddress, successfulPayment } from 'State/Checkout';
import { resetPromoCode } from 'State/PromoCode';
import {
  applyDefaultPromo,
  trackPaymentCompleted,
  trackPaymentError,
  selectPaymentMethodByType,
} from 'Utils/PaymentMethods';
import { logger } from 'Utils/Logger';
import { sendSheerVerificationId } from 'Utils/SheerIdHelpers';
import { clearSubscription } from 'State/Subscriptions';
import { getState, setState } from 'Utils/Store';
import { hideOverlay } from 'State/Layout';

export const paymentMethodsInitialState = {
  isSuccess: false,
  isFetching: false,
  isSubmitting: false,
  isPaymentFormValid: false,
  methods: [],
  hasPaymentMethods: false,
  hasCardPayment: false,
  hasPaypalPayment: false,
  hasApplePayPayment: false,
  cardPayment: undefined,
  paypalPayment: undefined,
  showPaymentMethod: undefined,
};

export const getPaymentMethodRequest = () => setState('paymentMethods', { isFetching: true });

export const getPaymentMethodSuccess = (payload) =>
  setState('paymentMethods', { ...payload, isSuccess: true, isFetching: false });

export const getPaymentMethodError = (payload) =>
  setState('paymentMethods', {
    ...paymentMethodsInitialState,
    errorMessage: payload,
    isSuccess: true,
    isFetching: false,
  });

export const clearPaymentMethods = () => setState('paymentMethods', paymentMethodsInitialState);

export const fetchPaymentMethods = () => {
  getPaymentMethodRequest();
  return getPaymentMethods()
    .then((data) => getPaymentMethodSuccess(data))
    .catch((error) => {
      const errorMessage = error.message || 'Error: unable to get payment method.';
      getPaymentMethodError(errorMessage);
    });
};

export const payment = async (
  paymentMethodType,
  paymentData,
  trackPaymentMethod,
  pageStartSourceData,
  pageFlowTypeData
) => {
  const { checkout, paymentMethods, plans, promoCode, purchaseMode, secondScreen, subscription, user } = getState();
  const isSkipDefaultPromo = !applyDefaultPromo({ subscription, promoCode, plans });
  // add transaction data here for prev apple pay session

  const payload = {
    promoCode,
    paymentMethods,
    purchaseMode,
    user,
    plans,
    checkout,
    subscription,
    isSkipDefaultPromo,
    secondScreen,
    ...paymentData,
  };
  const { selectedPlan } = plans;
  logger.started(`payment`, { trackPaymentMethod, isSkipDefaultPromo });
  let data = await selectPaymentMethodByType(paymentMethodType, payload);
  /* The below line exists because for now, it seems Apple Pay can return undefined here.
    Not sure why or in which circumstances yet.
    TODO: clean this up and not let ApplePay ever return undefined */
  data = data || {};
  const { isSuccess, orderID, errorCode, errorMessage } = data;
  console.debug(`payment processed: `, isSuccess, orderID, errorCode, errorMessage);
  if (isSuccess && orderID) {
    console.debug(`payment success: `, orderID);
    logger.completed(`payment`, { ...data, trackPaymentMethod });
    const { selectedPlan: { freePromo: { isFreeTrial = false } = {} } = {} } = plans;
    setSubscriptionStateProperty(isFreeTrial ? 'trialist' : 'new');
    setPaymentTypeProperty(paymentMethodType);
    successfulPayment();
    await getEntitlements();
    await sendSheerVerificationId();
    trackPaymentCompleted({
      trackPaymentMethod,
      selectedPlan,
      orderID,
      user,
      promoCode,
      pageStartSourceData,
      pageFlowTypeData,
    });

    clearSubscription();
    hideOverlay();
    resetPromoCode();
    resetShippingAddress();
    return Promise.resolve(orderID);
  }
  // payment error
  logger.error(`payment`, { ...data, trackPaymentMethod }, new Error(errorMessage));

  trackPaymentError({ trackPaymentMethod, user, errorCode, errorMessage });
  hideOverlay();
  const evergentErrorMessage = getErrorMessageFromCode(errorCode);
  return Promise.reject(Error(evergentErrorMessage || errorMessage));
};
